import { useEffect, useState, type FormEvent } from 'react'
import apiContract from '@product/api.json'
import webhookSchema from '@product/data/webhook-payload.schema.json'
import { api, ApiFailure, apiUrl, reportScreen, setAccessToken } from '../api.ts'
import { BackButton, Button, Field, Form, FormError, LinkButton } from '../components.tsx'
import { t } from '../contracts.ts'
import { authCallbackQuery } from '../oauth.ts'
import { navigate } from '../router.ts'
import type { Account } from '../types.ts'

/**
 * flows.json authMethodVisibility. A discovery failure must never produce a
 * screen with no way in, so the fallback keeps the one method that cannot be
 * configured away.
 */
export interface AuthMethods { emailCode: boolean; password: boolean; google: boolean }
const UNREACHABLE: AuthMethods = { password: true, google: false, emailCode: false }

function useAuthMethods(): AuthMethods | null {
  const [methods, setMethods] = useState<AuthMethods | null>(null)
  useEffect(() => {
    let live = true
    void api<AuthMethods>('/v1/auth/methods')
      .then((value) => { if (live) setMethods(value) })
      .catch(() => { if (live) setMethods(UNREACHABLE) })
    return () => { live = false }
  }, [])
  return methods
}

/** Where a sign-in lands: a pending deep link if there is one, else the inbox or the empty-state list. */
async function afterSignIn(): Promise<void> {
  const pending = sessionStorage.getItem('bellhook.pendingPath')
  sessionStorage.removeItem('bellhook.pendingPath')
  if (pending) return navigate(pending, true)
  const notifications = await api<{ items: unknown[] }>('/v1/notifications?limit=1')
  navigate(notifications.items.length ? '/inbox' : '/endpoints', true)
}

export function LandingScreen() {
  return <main className="hero"><div className="hero-inner"><div className="hero-mark" aria-hidden="true" /><h1>{t('landing.headline')}</h1><p>{t('landing.subhead')}</p><div className="hero-actions"><LinkButton to="/signin/password" tone="primary">{t('cta.getStarted')}</LinkButton><LinkButton to="/docs">{t('landing.footer.docs')}</LinkButton></div><footer className="footer-links"><span>{t('landing.footer.privacy')}</span><span>{t('landing.footer.terms')}</span></footer></div></main>
}

/**
 * The dashboard's front door. The password form is unconditional; the Google
 * button and the emailed-code link appear only when the deployment reports
 * they can complete.
 */
export function SignInPasswordScreen({ onSession }: { onSession: (account: Account) => void }) {
  const returnTo = new URLSearchParams(window.location.search).get('returnTo')
  if (returnTo?.startsWith('/join/')) sessionStorage.setItem('bellhook.pendingPath', returnTo)
  const methods = useAuthMethods() ?? UNREACHABLE
  const [email, setEmail] = useState(sessionStorage.getItem('bellhook.email') ?? '')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(() => {
    const message = sessionStorage.getItem('bellhook.sessionMessage')
    sessionStorage.removeItem('bellhook.sessionMessage')
    return message
  })

  async function submit(event: FormEvent) {
    event.preventDefault(); setSubmitting(true); setError(null)
    try {
      const session = await api<{ accessToken: string; account: Account }>('/v1/auth/password', {
        method: 'POST', body: JSON.stringify({ email, password }),
      })
      setAccessToken(session.accessToken); onSession(session.account)
      sessionStorage.setItem('bellhook.email', email)
      await afterSignIn()
    } catch (failure) {
      const code = failure instanceof ApiFailure ? failure.problem.code : ''
      // Neither field is marked: the server does not say which was wrong.
      setError(t(code === 'invalid_credentials' ? 'signInPassword.error.invalid' : code === 'rate_limited' ? 'signInPassword.error.tooMany' : 'signInPassword.error.failed'))
      void reportScreen('signInPassword', code || 'server_error')
      setSubmitting(false)
    }
  }

  return <main className="screen narrow"><h1>{t('signInPassword.title')}</h1><Form onSubmit={submit}>
    <Field label={t('signIn.emailLabel')}><input type="email" autoComplete="email" required value={email} placeholder={t('signIn.emailPlaceholder')} onChange={(event) => setEmail(event.target.value)} /></Field>
    <Field label={t('signInPassword.passwordLabel')}><input type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} /></Field>
    <FormError>{error}</FormError>
    <Button tone="primary" type="submit" disabled={submitting}>{submitting ? t('common.loading') : t('cta.signIn')}</Button>
    {methods.google && <><p className="auth-divider" aria-hidden="true">{t('signInPassword.divider')}</p><Button type="button" onClick={() => { window.location.assign(apiUrl('/v1/auth/google/start')) }}>{t('cta.continueWithGoogle')}</Button></>}
    {methods.emailCode && <Button type="button" tone="quiet" onClick={() => navigate('/signin')}>{t('signInPassword.emailCodeLink')}</Button>}
    <p>{t('signIn.legal')}</p>
  </Form></main>
}

/**
 * Transient landing for Google's redirect. No `ready` state: it opens in
 * `loading` and either resolves to a destination or shows `error` — it never
 * flashes the sign-in form behind the spinner. The query string is already
 * gone by the time this renders (see oauth.ts).
 */
export function AuthCallbackScreen({ onSession }: { onSession: (account: Account) => void }) {
  const [failed, setFailed] = useState(authCallbackQuery.error !== null)

  useEffect(() => {
    if (authCallbackQuery.error !== null) {
      void reportScreen('authCallback', 'oauth_failed')
      return
    }
    let live = true
    void (async () => {
      try {
        // The session arrived as an HttpOnly cookie, never in this URL.
        const session = await api<{ accessToken: string; account: Account }>('/v1/auth/refresh', { method: 'POST', body: '{}' })
        if (!live) return
        setAccessToken(session.accessToken); onSession(session.account)
        const pending = sessionStorage.getItem('bellhook.pendingPath') ?? authCallbackQuery.next
        sessionStorage.removeItem('bellhook.pendingPath')
        if (pending) return navigate(pending, true)
        const notifications = await api<{ items: unknown[] }>('/v1/notifications?limit=1')
        navigate(notifications.items.length ? '/inbox' : '/endpoints', true)
      } catch {
        if (!live) return
        setFailed(true)
        void reportScreen('authCallback', 'oauth_failed')
      }
    })()
    return () => { live = false }
  }, [onSession])

  if (failed) {
    return <main className="screen narrow"><div className="state-card" role="alert"><h1>{t('authCallback.error.title')}</h1><LinkButton to="/signin/password" tone="primary">{t('cta.signIn')}</LinkButton></div></main>
  }
  return <main className="screen narrow"><div className="state-card" role="status"><span className="spinner" /><h1>{t('authCallback.title')}</h1></div></main>
}

export function SignInScreen() {
  const returnTo = new URLSearchParams(window.location.search).get('returnTo')
  if (returnTo?.startsWith('/join/')) sessionStorage.setItem('bellhook.pendingPath', returnTo)
  // A deployment that cannot deliver mail must not offer a path that ends in
  // an unreadable code — the route stays live for the native clients either way.
  const methods = useAuthMethods()
  useEffect(() => { if (methods && !methods.emailCode) navigate('/signin/password', true) }, [methods])
  const [email, setEmail] = useState(sessionStorage.getItem('bellhook.email') ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(() => {
    const message = sessionStorage.getItem('bellhook.sessionMessage')
    sessionStorage.removeItem('bellhook.sessionMessage')
    return message
  })
  async function submit(event: FormEvent) {
    event.preventDefault(); setSubmitting(true); setError(null)
    try {
      await api<void>('/v1/auth/code', { method: 'POST', body: JSON.stringify({ email }) })
      sessionStorage.setItem('bellhook.email', email)
      navigate('/signin/verify')
    } catch (failure) {
      const code = failure instanceof ApiFailure ? failure.problem.code : ''
      setError(t(code === 'invalid_email' ? 'signIn.error.invalidEmail' : code === 'rate_limited' ? 'signIn.error.tooMany' : 'signIn.error.failed'))
    } finally { setSubmitting(false) }
  }
  if (!methods) return <main className="screen narrow"><div className="state-card" role="status"><span className="spinner" aria-label={t('common.loading')} /></div></main>
  return <main className="screen narrow"><BackButton fallback="/" /><h1>{t('signIn.title')}</h1><Form onSubmit={submit}><Field label={t('signIn.emailLabel')}><input type="email" autoComplete="email" required value={email} placeholder={t('signIn.emailPlaceholder')} onChange={(event) => setEmail(event.target.value)} /></Field><FormError>{error}</FormError><Button tone="primary" type="submit" disabled={submitting}>{submitting ? t('common.loading') : t('cta.sendCode')}</Button><p>{t('signIn.legal')}</p></Form></main>
}

export function VerifyCodeScreen({ onSession }: { onSession: (account: Account) => void }) {
  const email = sessionStorage.getItem('bellhook.email') ?? ''
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function submit(event: FormEvent) {
    event.preventDefault(); setSubmitting(true); setError(null)
    try {
      const session = await api<{ accessToken: string; account: Account }>('/v1/auth/verify', { method: 'POST', body: JSON.stringify({ email, code }) })
      setAccessToken(session.accessToken); onSession(session.account)
      const pending = sessionStorage.getItem('bellhook.pendingPath')
      sessionStorage.removeItem('bellhook.pendingPath')
      if (pending) return navigate(pending, true)
      const notifications = await api<{ items: unknown[] }>('/v1/notifications?limit=1')
      navigate(notifications.items.length ? '/inbox' : '/endpoints', true)
    } catch (failure) {
      const problem = failure instanceof ApiFailure ? failure.problem.code : ''
      setError(t(problem === 'code_expired' ? 'verifyCode.error.expired' : problem === 'rate_limited' ? 'verifyCode.error.tooMany' : 'verifyCode.error.invalid'))
    } finally { setSubmitting(false) }
  }
  async function resend() {
    setError(null)
    try { await api<void>('/v1/auth/code', { method: 'POST', body: JSON.stringify({ email }) }); setError(t('verifyCode.resent')) }
    catch { setError(t('signIn.error.failed')) }
  }
  return <main className="screen narrow"><BackButton fallback="/signin" /><h1>{t('verifyCode.title')}</h1><p>{t('verifyCode.subhead', { email })}</p><Form onSubmit={submit}><Field label={t('verifyCode.codeLabel')}><input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} /></Field><FormError>{error}</FormError><Button tone="primary" type="submit" disabled={submitting || code.length !== 6}>{submitting ? t('common.loading') : t('cta.verify')}</Button><Button type="button" tone="quiet" onClick={resend}>{t('cta.resendCode')}</Button></Form></main>
}

export function DocsScreen() {
  const fields = Object.entries(webhookSchema.properties) as [string, { type?: string | string[]; maxLength?: number; maxItems?: number }][]
  const local = apiContract.baseUrl.local.replace('{tk.bridge.localHost}', 'localhost')
  const snippet = `curl -X POST ${local}/h/{key} \\\n  -H 'Content-Type: application/json' \\\n  -d '{"title":"Deploy complete"}'`
  return <main className="screen narrow"><BackButton fallback="/" /><h1>{t('docs.title')}</h1><section className="card detail-grid"><h2>{t('docs.sendHeader')}</h2><pre className="code">{snippet}</pre><Button onClick={() => void navigator.clipboard.writeText(snippet)}>{t('cta.copy')}</Button><h2>{t('docs.payloadHeader')}</h2><table><thead><tr><th>{t('docs.column.field')}</th><th>{t('docs.column.type')}</th><th>{t('docs.column.notes')}</th></tr></thead><tbody>{fields.map(([name, definition]) => <tr key={name}><td><code>{name}</code></td><td><code>{Array.isArray(definition.type) ? definition.type.join(' | ') : definition.type}</code></td><td><code>{definition.maxLength ?? definition.maxItems ?? ''}</code></td></tr>)}</tbody></table><h2>{t('docs.signingHeader')}</h2><pre className="code">{apiContract.signing.signedPayload}</pre></section></main>
}

export function NotFoundScreen({ signedIn }: { signedIn: boolean }) {
  return <main className="screen narrow"><div className="state-card"><h1>{t('notFound.title')}</h1><LinkButton to={signedIn ? '/inbox' : '/'} tone="primary">{t('cta.goHome')}</LinkButton></div></main>
}
